import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

import { cn } from "@/app/lib/utils";
import type { GroupedSkill } from "../../../components/ResumeBuilder/SkillsEditorListItems/SkillsEditorListItems";
import CustomInputSkill from "./CustomInputSkill";
import SingleSkillItem from "./SingleSkillItem";

interface GroupedSkillsItemProps {
	item: GroupedSkill;
	collapseGroupedSkills: (groupedSkillIndex: number) => void;
	groupedSkillIndex: number;
	removeGroupedSkills: (skillTitle: string) => void;
	onSaveCustomSkillAdded: (groupedSkillIndex: number, singleSkillIndex: number, skillAdded: string) => void;
	onRemoveSingleSkill: (groupedSkillIndex: number, singleSkillIndex: number) => void;
	onAddCustomSkill: (groupedSkillIndex: number) => void;
}

function GroupedSkillsItem({
	item,
	collapseGroupedSkills,
	groupedSkillIndex,
	removeGroupedSkills,
	onSaveCustomSkillAdded,
	onRemoveSingleSkill,
	onAddCustomSkill,
}: GroupedSkillsItemProps) {
	// Fills the column it is given. It used to cap itself at 508px, which left it
	// narrower than everything it sits under — the description editor on a job, the
	// section's own buttons on the Skills page.
	return (
		<div
			className={cn(
				"mb-3 box-border flex flex-col items-start justify-end self-stretch rounded-md border border-[#d5dbe1] px-4 py-2",
				item.collapsed && "cursor-pointer bg-[#eff9ff]",
			)}
			onClick={() => item.collapsed && collapseGroupedSkills(groupedSkillIndex)}
		>
			<div className="flex w-full flex-col items-start justify-center gap-3 self-stretch pt-2 pb-3">
				<div className="flex w-full flex-row items-center gap-3 self-stretch">
					{/* Chevron and name are one target, so folding a category is the width of
					    the row rather than a 16px icon. Collapsed, the click passes through to
					    the card, which is what opens it. */}
					<button
						type="button"
						className="mr-auto flex cursor-pointer items-center gap-2 text-left"
						aria-expanded={!item.collapsed}
						onClick={() => !item.collapsed && collapseGroupedSkills(groupedSkillIndex)}
					>
						{item.collapsed ? <ChevronDown className="size-4 shrink-0" /> : <ChevronUp className="size-4 shrink-0" />}
						<span className="font-semibold text-black text-sm leading-5">{item.title}</span>
					</button>
					{/* Stops the click here. The card behind expands whatever it is given, so a
					    remove that reached it would be written straight back off the list it
					    had before the removal. */}
					<button
						type="button"
						className="shrink-0 cursor-pointer"
						aria-label={`Remove ${item.title}`}
						onClick={(event) => {
							event.stopPropagation();
							removeGroupedSkills(item.title);
						}}
					>
						<Trash2 className="size-4" />
					</button>
				</div>
				{!item.collapsed && (
					<>
						<div className="flex w-full flex-row flex-wrap content-start items-center gap-3 self-stretch">
							{item.skills.map((singleSkill, singleSkillIndex) => {
								if (singleSkill.skill === "") {
									return (
										<CustomInputSkill
											key={singleSkillIndex}
											groupedSkillIndex={groupedSkillIndex}
											singleSkillIndex={singleSkillIndex}
											onSaveCustomSkillAdded={onSaveCustomSkillAdded}
										/>
									);
								}
								return (
									<SingleSkillItem
										key={singleSkill.skill}
										onRemoveSingleSkill={onRemoveSingleSkill}
										singleSkill={singleSkill}
										groupedSkillIndex={groupedSkillIndex}
										singleSkillIndex={singleSkillIndex}
									/>
								);
							})}
						</div>
						<div
							className="flex cursor-pointer items-center"
							onClick={() => {
								onAddCustomSkill(groupedSkillIndex);
							}}
						>
							<Plus className="mr-2 size-4 text-[#019ce0]" />
							<span className="font-semibold text-[#019ce0] text-sm leading-5">Add custom</span>
						</div>
					</>
				)}
			</div>
		</div>
	);
}

export default GroupedSkillsItem;
